import $api from 'http/index';
import { getOptions } from 'helpers/getOptions';
import superadminActions from './superadminActions';
import settingsAction from './../settings/settingsAction';
import personalProfileActions from '../personalProfile/personalProfileActions';
import { downloadMultipleFiles } from 'helpers/downloadFile';
import { throwIfPtoConflict } from 'helpers/ptoConflict';

export const catchError = (error) => {
  if (error?.response?.data?.message) {
    if (Array.isArray(error.response.data.message))
      return error.response.data.message.join('\n');
    else  return error.response.data.message;
  } else {
    return error.message;
  }
};

export const fetchClients = (filters = {}) => async (dispatch) => {
  try {
    const options = {
      params: {
        subscription: filters?.subscription,
        active: filters?.active,
      }
    }

    const response = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/organizations`,
      { ...options, ...getOptions(), }
    );

    dispatch(superadminActions.getClients(response.data));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
};


// deliberately not a thunk: writing to state.superadmin.clients would clobber the Clients table
export const searchClients = async (searchValue) => {
  const response = await $api.get(`${process.env.REACT_APP_BASE_URL}/organizations`, {
    params: { lean: true, limit: 25, searchValue },
    ...getOptions(),
  });

  return response.data;
};

export const postNewClient = (data) => async (dispatch) => {
  try {
    const response = await $api.post(
      `${process.env.REACT_APP_BASE_URL}/organizations`,
      data,
      { ...getOptions(), }
    );

    dispatch(superadminActions.createClient(response.data));
    dispatch(superadminActions.snackBarInfo({type: 'success', text: 'Company successfully added'}));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
};


export const putClient = (clientId, data) => async (dispatch, getState) => {
  const currentOrganizationId = getState().personalProfile?.organization?._id || '';
  try {
    const response = await $api.put(
      `${process.env.REACT_APP_BASE_URL}/organizations/${clientId}`,
      data,
      { ...getOptions(), },
    );

    dispatch(superadminActions.updateClient(response.data));
    dispatch(superadminActions.snackBarInfo({type: 'success', text: 'Company successfully updated'}));

    if( clientId === currentOrganizationId ){
      dispatch(settingsAction.updateSettings(response.data.settings));
      dispatch(personalProfileActions.setOrganization(response.data));
    }
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
};


export const fetchStatistic = (date, clientId) => async (dispatch) => {
  const options = {
    params: { date: date?.toISOString() }
  };
  if (clientId && clientId !== 'All') options.params.organizationId = clientId
  try {
    const response = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/homepage/admin-panel`,
      { ...options, ...getOptions(), },
    );

    dispatch(superadminActions.getStatistic(response.data));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
}


export const loginToOrganization = (_id) => async (dispatch) => {
  try {
    const response = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/organizations/${_id}/login`,
      { ...getOptions() }
    );

    const { organization, organizationLocation } = response.data;

    dispatch(superadminActions.snackBarInfo({ type: 'success', text: `Succesfully logged in to ${organization.name}` }));
    dispatch(personalProfileActions.setOrganization(organization))
    dispatch(personalProfileActions.setOrganizationLocation(organizationLocation))
    dispatch(settingsAction.getSettings(organization.settings));
    dispatch(settingsAction.getSettings(organizationLocation.settings))
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
}


export const fetchUsers = (filters = {}, searchValue, sortBy, sortOrder) => async (dispatch) => {
  try {
    const options = {
      params: {
        ...filters,
      }
    };
    if (sortBy) options.params.sortBy = sortBy;
    if (sortOrder) options.params.sortOrder = sortOrder;
    if (searchValue) options.params.searchValue = searchValue;

    const response = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/users/admin-panel`,
      { ...options, ...getOptions(), }
    );

    dispatch(superadminActions.getUsers(response.data));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
}; 

export const postAssignUsers = (userIds, data) => async (dispatch) => {
  try {
    const body = {
      ...data,
      userIds,
    }
    const response = await $api.post(
      `${process.env.REACT_APP_BASE_URL}/users/admin-panel/assign-organization`,
      body,
      { ...getOptions() },
    );

    dispatch(superadminActions.updateBunchUsers(response.data));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
}

export const postResetPassword = (userIds) => async (dispatch) => {
  try {
    await $api.post(
      `${process.env.REACT_APP_BASE_URL}/users/admin-panel/reset-password`,
      { userIds },
      { ...getOptions() },
    );

    dispatch(superadminActions.snackBarInfo({ type: 'success', text: 'Emails succesfully sent' }));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
}

export const putUser = (userId, data, isForcePTO = false) => async (dispatch) => {
  try {
    const response = await $api.put(
      `${process.env.REACT_APP_BASE_URL}/users/admin-panel/${userId}`,
      { ...data, ...(isForcePTO ? { isForcePTO: true } : {}) },
      { ...getOptions(), },
    );

    dispatch(superadminActions.updateUser(response.data));
    dispatch(superadminActions.snackBarInfo({type: 'success', text: 'User successfully updated'}));
  } catch (error) {
    throwIfPtoConflict(error);
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
};

export const postUser = (data) => async (dispatch) => {
  try {
    const response = await $api.post(
      `${process.env.REACT_APP_BASE_URL}/users/admin-panel`,
      data,
      { ...getOptions(), },
    );

    dispatch(superadminActions.createUser(response.data));
    dispatch(superadminActions.snackBarInfo({type: 'success', text: 'User successfully created'}));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299){
      throw new Error(catchError(error))};
  }
};


export const deleteUser = (userId) => async (dispatch) => {
  try {
    await $api.delete(
      `${process.env.REACT_APP_BASE_URL}/users/admin-panel/${userId}`,
      { ...getOptions(), },
    );

    dispatch(superadminActions.deleteUser(userId));
    dispatch(superadminActions.snackBarInfo({type: 'success', text: 'User successfully deleted'}));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
};


export const exportUsers = (filters, searchValue, sortBy, sortOrder, organizationName) => async (dispatch) => {
  try {
    const options = {
      params: {
        ...filters,
      }
    };
    if (sortBy) options.params.sortBy = sortBy;
    if (sortOrder) options.params.sortOrder = sortOrder;
    if (searchValue) options.params.searchValue = searchValue;
    const searchParams = new URLSearchParams(options.params).toString();

    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/users/admin-panel/export-csv?${searchParams}`,
      { headers: { ...(getOptions().headers), 'Accept': 'multipart/form-data' }, method: 'GET' },
    )
    if (response.status >= 300) throw new Error('Something went wrong');

    const formData = await response.formData();

    const files = [];
    for (const pair of formData.entries()) {
      files.push({ name: `${pair[0]}.csv`, blob: new Blob([pair[1]], { type: 'text/csv' }),  });
    }
    if (!files.length) {
      dispatch(superadminActions.snackBarInfo({type: 'success', text: 'Nothing to export' }));
    }

    await downloadMultipleFiles(files);
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
}


export const importUsers = (data) => async (dispatch) => {
  try {
    const response = await $api.post(
      `${process.env.REACT_APP_BASE_URL}/users/admin-panel/import-csv`,
      data,
      { ...getOptions('multipart/form-data') }
    );

    dispatch(superadminActions.snackBarInfo({ type: 'success', text: `Imported ${response.data.createdUsers} users` }));
  } catch (error) {
    dispatch(superadminActions.snackBarInfo({type: 'error', text: catchError(error)}));
    if (error.response.data.statusCode > 299) throw new Error(catchError(error));
  }
};


// HI Codes

export const fetchHICodesByTenant = () => async (dispatch) => {
  try {
    dispatch(superadminActions.setHICodesLoading(true));

    const response = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/hi-codes`,
      { headers: { ...getOptions().headers }, params: { adminPanel: true } }
    );

    dispatch(superadminActions.setHICodesByTenant(response.data));
  } catch (error) {
    catchError(error);
  } finally {
    dispatch(superadminActions.setHICodesLoading(false));
  }
}

export const postHICodesByTenant = (tenantId, hiCode) => async (dispatch) => {
  try {
    if (!tenantId) throw new Error('Organization must be selected');

    dispatch(superadminActions.setHICodesLoading(true));

    const body = { name: hiCode.name, tenantId };
    const response = await $api.post(
      `${process.env.REACT_APP_BASE_URL}/hi-codes`,
      body,
      { headers: { ...getOptions().headers } }
    );

    dispatch(superadminActions.addHICodeByTenant({ tenantId, data: response.data}));
  } catch (error) {
    catchError(error);
  } finally {
    dispatch(superadminActions.setHICodesLoading(false));
  }
}


export const putHICodesByTenant = (tenantId, updatedHICode) => async (dispatch) => {
  try {
    if (!tenantId) throw new Error('Organization must be selected');

    dispatch(superadminActions.setHICodesLoading(true));

    const body = { name: updatedHICode.name, tenantId };
    const response = await $api.put(
      `${process.env.REACT_APP_BASE_URL}/hi-codes/${updatedHICode._id}`,
      body,
      { headers: { ...getOptions().headers } }
    );

    dispatch(superadminActions.updateHICodeByTenant({ tenantId, data: response.data }));
  } catch (error) {
    catchError(error);
  } finally {
    dispatch(superadminActions.setHICodesLoading(false));
  }
}


export const deleteHICodesByTenant = (tenantId, hiCodeId) => async (dispatch) => {
  try {
    if (!tenantId) throw new Error('Organization must be selected');

    dispatch(superadminActions.setHICodesLoading(true));

    const response = await $api.delete(
      `${process.env.REACT_APP_BASE_URL}/hi-codes/${hiCodeId}`,
      { headers: { ...getOptions().headers } }
    );

    dispatch(superadminActions.deleteHICodeByTenant({ tenantId, data: { _id: hiCodeId } }));
  } catch (error) {
    catchError(error);
  } finally {
    dispatch(superadminActions.setHICodesLoading(false));
  }
}